import { yupResolver } from '@hookform/resolvers/yup'
import { Button, TextInput } from '@telavita-core/react-design-kit'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import MedicalRequestService from 'services/medicalRequest'
import { createPatientTreatment } from 'services/patients/createPatientTreatment'
import { DiagnosticHypothesesSelect } from './components/DiagnosticHypothesesSelect'
import { DocumentFileInput } from './components/DocumentFileInput'
import { IssueDateInput } from './components/IssueDateInput'
import { ProfessionalDocumentInput } from './components/ProfessionalDocumentInput'
import { ProfessionalDocumentStateSelect } from './components/ProfessionalDocumentStateSelect'
import { ProfessionalNameInput } from './components/ProfessionalNameInput'
import { SessionQuantityInput } from './components/SessionQuantityInput'
import { SessionQuantityRadio } from './components/SessionQuantityRadio'
import { SpecialtySelect } from './components/SpecialtySelect'
import { MedicalAppointmentFormData, medicalAppointmentSchema } from './schema'

import { UserData } from 'models/Auth'
import { useSelector } from 'react-redux'
import { getProcedureCode } from 'services/patients/getProcedureCode'
import { createAuthorizationRequestForTreatment } from 'services/patients/createAuthorizationRequestForTreatment'
import formatters from './formatters'
import * as S from './styles'

interface MedicalRecordFormProps {
  onSuccess?: () => void;
  onError?: () => void;
  planCode: string;
}

export const MedicalRequestForm = ({
  onSuccess,
  onError,
  planCode,
}: MedicalRecordFormProps) => {
  const userData = useSelector((state: UserData) => state.auth.userData)
  const [submitLoading, setSubmitLoading] = React.useState(false)
  const formMethods = useForm<MedicalAppointmentFormData>({
    resolver: yupResolver(medicalAppointmentSchema),
    mode: 'onChange',
  })

  const { handleSubmit, watch } = formMethods
  const specialty = watch('specialty')
  const hasSessionQuantity = watch('hasSessionQuantity') === 'true'

  const handleSubmitData = async (data: MedicalAppointmentFormData) => {
    try {
      setSubmitLoading(true)
      const medicalRequestPayload = await formatters.formatMedicalRequest(
        data,
        planCode
      )
      const medicalRequestResponse = await MedicalRequestService.createMedicalRequest(
        {
          username: userData?.username,
          data: medicalRequestPayload,
        }
      )

      const treatment = await createPatientTreatment({
        username: userData.username,
        data: {
          medical_request_uuid: medicalRequestResponse.uuid,
          plan_code: planCode,
        },
      })

      const procedureCodeData = await getProcedureCode({
        treatmentId: treatment.id,
      })

      await createAuthorizationRequestForTreatment({
        treatmentId: treatment.id,
        data: { procedure_code: procedureCodeData.code },
      })

      onSuccess && onSuccess()
    } catch (error) {
      onError && onError()
      console.log('Erro ao criar solicitação médica', error)
    } finally {
      setSubmitLoading(false)
    }
  }

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(handleSubmitData)}>
        <S.Container>
          <DocumentFileInput />
          <IssueDateInput />
          <DiagnosticHypothesesSelect />
          <SessionQuantityRadio />
          {hasSessionQuantity && <SessionQuantityInput />}
          <ProfessionalNameInput />
          <SpecialtySelect />
          {specialty?.length > 0 && (
            <>
              <S.WrapperField>
                <S.DocumentTypeWrapper>
                  <TextInput
                    name="specialtySelected"
                    onInputChange={undefined}
                    value={specialty[0]?.type}
                    disabled
                  />
                </S.DocumentTypeWrapper>
                <ProfessionalDocumentInput />
              </S.WrapperField>
              <ProfessionalDocumentStateSelect />
            </>
          )}
          <S.WrapperButton>
            <Button disabled={submitLoading} variant="contained" uppercase>
              Enviar e prosseguir
            </Button>
          </S.WrapperButton>
        </S.Container>
      </form>
    </FormProvider>
  )
}
